import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, MessageCircle, Star, Camera, MapPin, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { COMPANY, PROJECT_TYPES, IMAGES } from "@/lib/company";
import { submitLead, submitLeadWithFiles } from "@/lib/leadApi";
import { trackEstimate, trackWhatsApp, trackLeadSubmit } from "@/lib/track";

export default function Hero() {
  const [form, setForm] = useState({ name: "", phone: "", project_type: "", location: "", message: "" });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const fileRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and WhatsApp number.");
      return;
    }
    setSubmitting(true);
    try {
      if (files.length) {
        const fd = new FormData();
        Object.entries({ ...form, source: "hero" }).forEach(([k, v]) => fd.append(k, v));
        files.forEach((f) => fd.append("files", f));
        await submitLeadWithFiles(fd);
      } else {
        await submitLead({ ...form, source: "hero" });
      }
      trackLeadSubmit("hero");
      // OpenAI Ads Pixel — Lead conversion ("Renovation Estimate Lead").
      // Fires exactly once per successful submission, only after the API
      // request resolves successfully. The `submitting` re-entry guard and
      // the disabled submit button prevent duplicates from double-clicks.
      if (typeof window.oaiq === "function") {
        window.oaiq("measure", "lead_created", { type: "customer_action" });
      }
      setSuccess(true);
      toast.success("Thank you. We'll review your details and come back with a rough estimate.");
      setForm({ name: "", phone: "", project_type: "", location: "", message: "" });
      setFiles([]);
    } catch (err) {
      toast.error("Unable to submit. Please WhatsApp us your photos instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative flex items-center pt-24 lg:pt-28 pb-14 lg:pb-24 lg:min-h-[100vh]"
    >
      <div className="absolute inset-0 z-0">
        <img
          src={IMAGES.hero}
          alt="Luxury apartment and home renovation Dubai by Fix Technical Renovation Company"
          className="w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/35" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent lg:hidden" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center w-full">
        <div className="lg:col-span-7 text-white">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#C5A059] text-[#E2C988] text-[11px] uppercase tracking-[0.2em] mb-4 font-medium">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#C5A059]" />
            Renovation Contractor · Dubai
          </div>

          <div className="inline-flex items-center gap-2.5 bg-white/10 backdrop-blur border border-white/20 px-3 py-1.5 mb-6 ml-0 sm:ml-3">
            <div className="flex items-center gap-0.5" aria-label="5 out of 5 stars">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="w-3.5 h-3.5 fill-[#F4B400] text-[#F4B400]" />
              ))}
            </div>
            <span className="text-[11px] text-white font-medium">5.0 · Verified Google Reviews</span>
          </div>

          {/* H1 — approved copy, do not change */}
          <h1
            data-testid="hero-h1"
            className="font-heading text-3xl sm:text-4xl lg:text-[42px] font-light tracking-tight leading-[1.1] mb-4"
          >
            Fix Technical Renovation Company: Luxury Interior Fitting &amp; Renovations Dubai
          </h1>

          {/* Approved main description */}
          <h2
            data-testid="hero-subheadline"
            className="font-heading text-2xl sm:text-3xl lg:text-[38px] font-medium text-[#E2C988] leading-[1.15] mb-5"
          >
            Apartment &amp; Home Renovation Dubai — Complete Renovation, Clear Pricing, No Surprises.
          </h2>

          <p className="text-base sm:text-lg text-neutral-200 leading-relaxed max-w-2xl mb-6 font-light">
            From demolition to final finishing, we handle your apartment or home renovation under one professional team — including kitchens, bathrooms, flooring, ceilings, painting, electrical, plumbing and complete interior fit-out.
          </p>

          {/* Rough estimate offer */}
          <div className="border-l-2 border-[#C5A059] bg-white/5 backdrop-blur-sm px-5 py-4 mb-7 max-w-2xl" data-testid="hero-estimate-offer">
            <p className="flex items-center gap-2 text-sm sm:text-base font-semibold tracking-wide text-white mb-1.5">
              <Camera className="w-4 h-4 text-[#C5A059] shrink-0" />
              GET YOUR ROUGH RENOVATION ESTIMATE
            </p>
            <p className="text-sm text-neutral-300 leading-relaxed mb-2">
              Send us photos or videos of your apartment, home or property and we&apos;ll review the available information and provide a rough preliminary renovation estimate.
            </p>
            <p className="flex items-center gap-2 text-xs sm:text-sm font-medium text-[#E2C988] tracking-wide">
              <MapPin className="w-3.5 h-3.5 shrink-0" />
              FREE SITE INSPECTION → ACTUAL PRICE
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mb-5">
            <a href="#lead-form" data-testid="hero-cta-estimate" onClick={() => trackEstimate("hero")}>
              <Button className="bg-[#C5A059] hover:bg-[#A88746] text-white rounded-none px-7 py-7 text-base font-semibold w-full sm:w-auto tracking-wide">
                GET MY ROUGH ESTIMATE
              </Button>
            </a>
            <a
              href={COMPANY.estimateLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              onClick={() => trackWhatsApp("hero")}
            >
              <Button
                variant="outline"
                className="border-[#25D366]/70 text-white hover:bg-[#25D366] hover:border-[#25D366] hover:text-white rounded-none px-7 py-7 text-base font-medium bg-transparent w-full sm:w-auto tracking-wide"
              >
                <MessageCircle className="w-5 h-5 mr-2" /> WHATSAPP US
              </Button>
            </a>
          </div>

          <p className="text-xs sm:text-sm text-neutral-300" data-testid="hero-reassurance">
            Photos &amp; Videos → Rough Estimate → Free Site Inspection → Detailed Quotation
          </p>
        </div>

        <div className="lg:col-span-5">
          <div id="lead-form" data-testid="hero-lead-form" className="bg-white p-7 lg:p-8 shadow-2xl border border-neutral-100 max-w-md mx-auto lg:ml-auto">
            {success ? (
              <div className="text-center py-10" data-testid="hero-form-success">
                <CheckCircle2 className="w-14 h-14 text-[#C5A059] mx-auto mb-4" />
                <h3 className="font-heading text-2xl font-light text-neutral-900 mb-2">Thank you</h3>
                <p className="text-neutral-600 text-sm">
                  Our team will review your details and come back with a rough renovation estimate.
                </p>
                <Button data-testid="hero-form-reset" onClick={() => setSuccess(false)} variant="outline" className="mt-6 rounded-none border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-white">
                  Submit Another Request
                </Button>
              </div>
            ) : (
              <>
                <div className="mb-5">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-[#C5A059] font-semibold mb-2">
                    Rough Estimate
                  </p>
                  <h3 className="font-heading text-2xl font-light text-neutral-900 leading-tight">
                    Get Your Rough Renovation Estimate
                  </h3>
                  <p className="text-xs text-neutral-500 mt-1.5">
                    Photos &amp; videos → rough estimate · Free site inspection → actual price
                  </p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="hero-form">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-neutral-500 uppercase tracking-wider">Name</label>
                      <Input
                        data-testid="hero-input-name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Your name"
                        className="rounded-none border-0 border-b border-neutral-200 px-0 focus-visible:ring-0 focus-visible:border-[#C5A059] h-11"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-neutral-500 uppercase tracking-wider">WhatsApp Number</label>
                      <Input
                        data-testid="hero-input-phone"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="+971 5X XXX XXXX"
                        className="rounded-none border-0 border-b border-neutral-200 px-0 focus-visible:ring-0 focus-visible:border-[#C5A059] h-11"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-neutral-500 uppercase tracking-wider">Property Type</label>
                      <Select value={form.project_type} onValueChange={(v) => setForm({ ...form, project_type: v })}>
                        <SelectTrigger data-testid="hero-input-project-type" className="rounded-none border-0 border-b border-neutral-200 px-0 focus:ring-0 h-11">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {PROJECT_TYPES.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-neutral-500 uppercase tracking-wider">Property Location</label>
                      <Input
                        data-testid="hero-input-location"
                        value={form.location}
                        onChange={(e) => setForm({ ...form, location: e.target.value })}
                        placeholder="e.g. Dubai Marina"
                        className="rounded-none border-0 border-b border-neutral-200 px-0 focus-visible:ring-0 focus-visible:border-[#C5A059] h-11"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-neutral-500 uppercase tracking-wider">What would you like to renovate?</label>
                    <Input
                      data-testid="hero-input-message"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="e.g. Full apartment, kitchen + 2 bathrooms"
                      className="rounded-none border-0 border-b border-neutral-200 px-0 focus-visible:ring-0 focus-visible:border-[#C5A059] h-11"
                    />
                  </div>

                  <div>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*,video/*"
                      multiple
                      className="hidden"
                      data-testid="hero-input-files"
                      onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 5))}
                    />
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      data-testid="hero-upload-trigger"
                      className="w-full border border-dashed border-neutral-300 hover:border-[#C5A059] transition-colors px-4 py-3 flex items-center justify-center gap-2 text-sm text-neutral-600"
                    >
                      <Upload className="w-4 h-4 text-[#C5A059]" />
                      {files.length ? `${files.length} photo/video${files.length > 1 ? "s" : ""} attached` : "Upload Photos / Videos (optional)"}
                    </button>
                    {files.length > 0 && (
                      <button
                        type="button"
                        onClick={() => { setFiles([]); if (fileRef.current) fileRef.current.value = ""; }}
                        data-testid="hero-upload-clear"
                        className="mt-1.5 text-xs text-neutral-400 hover:text-neutral-600 inline-flex items-center gap-1"
                      >
                        <X className="w-3 h-3" /> Remove attachments
                      </button>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="hero-form-submit"
                    className="w-full bg-[#C5A059] hover:bg-[#A88746] text-white rounded-none py-6 font-semibold tracking-tight mt-2 text-base"
                  >
                    {submitting ? "Submitting…" : "GET MY ROUGH ESTIMATE →"}
                  </Button>
                  <a href={COMPANY.estimateLink} target="_blank" rel="noopener noreferrer" className="block" onClick={() => trackWhatsApp("hero_form")}>
                    <Button type="button" variant="outline" className="w-full rounded-none border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white py-5 font-medium">
                      <MessageCircle className="w-4 h-4 mr-2" /> WHATSAPP US
                    </Button>
                  </a>
                  <p className="text-[10px] text-neutral-400 text-center leading-relaxed">
                    Photos/videos provide a rough estimate only — the actual price is confirmed after a free site inspection and detailed scope assessment.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
